import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Button } from './Button';
import { CHECKOUT_URL, HERO_IMAGE, HERO_IMAGE_ALT } from '../data/offer';
import { trackCheckout } from '../utils/tracking';
const negatives = ['Not a random collection of dental videos.', 'Not a pile of old lecture slides.', 'Not a text-heavy textbook you have to turn into classes.', 'Not a pile of resources you have to organize yourself.'];
const teachingFlow = ['OPEN THE SYSTEM.', 'CHOOSE THE TOPIC.', 'SHOW THE VISUAL.', 'TEACH THE SESSION.', 'RUN THE PRACTICE.', 'CHECK COMPETENCY.'];
const teachingAreas = [{
  number: '01',
  step: 'DENTAL ANATOMY, ORAL HISTOLOGY & TOOTH MORPHOLOGY',
  detail: 'Dentitions, numbering systems, tooth structure, histology, tooth-by-tooth morphology, occlusion and the TMJ.'
}, {
  number: '02',
  step: 'ORAL PATHOLOGY, DENTAL RADIOLOGY & CLINICAL DIAGNOSIS',
  detail: 'Clinical examination, caries, oral lesions, periodontal conditions, radiographic technique and interpretation.'
}, {
  number: '03',
  step: 'RESTORATIVE DENTISTRY, DENTAL MATERIALS & OPERATIVE TECHNIQUES',
  detail: 'Instruments, isolation, cavity preparations, composites, adhesion, cements, finishing and polishing.'
}, {
  number: '04',
  step: 'ENDODONTICS, PERIODONTICS & CLINICAL TREATMENT PROCEDURES',
  detail: 'Access, working length, cleaning and shaping, obturation, probing, charting, scaling and root planing.'
}, {
  number: '05',
  step: 'ORAL SURGERY, LOCAL ANESTHESIA & PATIENT MANAGEMENT',
  detail: 'Anesthetic agents and injection techniques, extractions, suturing, complications and medical emergencies.'
}, {
  number: '06',
  step: 'DENTAL CLINICAL INSTRUCTION, PROGRAMS & ASSESSMENTS',
  detail: 'Lesson design, demonstration guides, simulation labs, program plans, competency checklists and rubrics.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-[#F2F7F8] relative overflow-hidden" data-id="element-282" data-mp="2hpnw949exfie">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-[#0B7A7A]" data-id="element-283" data-mp="2cr30pi53vk0a" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-284" data-mp="2qihkvsar1r8x">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-285" data-mp="2mhxjudn223hi">
          <p className="text-sm md:text-base font-semibold text-[#0A6464] mb-4" data-id="element-286" data-mp="1tt9x0dmnr61u">
            The Complete Visual Dentistry Training System for Dental Instructors, Clinical Faculty &amp; Dental Educators
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-[#0F4C5C]" data-id="element-287" data-mp="22tgf6ctawu7c">
            DENTAL INSTRUCTOR <span className="text-[#0B7A7A]" data-id="element-288" data-mp="3j76n5unjk2m9">ATLAS</span>
          </h2>
          <p className="text-base md:text-lg text-[#0F4C5C] font-semibold mb-6" data-id="element-289" data-mp="3e9dg6zeez4kx">
            A Complete 6-IN-1 Visual Dentistry Teaching &amp; Clinical Training System
          </p>
          <img src={HERO_IMAGE} alt={HERO_IMAGE_ALT} className="w-full max-w-sm h-auto mx-auto mb-6 drop-shadow-xl" loading="lazy" data-id="element-290" data-mp="3msi5efipv55a" />
          <p className="text-base md:text-lg text-[#0A6464] font-semibold mb-10" data-id="element-291" data-mp="3cu2bk4sb1hs3">
            6 PROFESSIONAL VOLUMES · 300 VISUAL TRAINING PAGES · 5 EXCLUSIVE BONUSES
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-292" data-mp="9dljug8enng9">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-slate-200" data-id="element-293" data-mp="e90wx5yl9fbm">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-294" data-mp="x0tmsrid2kf4">
                <X className="text-[#0F4C5C]/50 shrink-0 mt-1" size={18} data-id="element-295" data-mp="x24s4vrgg08c" />
                <p className="text-slate-700/80 text-sm" data-id="element-296" data-mp="34l6dcuxykwgy">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-[#0F4C5C]" data-id="element-297" data-mp="30kw85khzqa35">
            <div className="flex items-start gap-3" data-id="element-298" data-mp="3o7ktkg67kkoi">
              <Check className="text-[#0B7A7A] shrink-0 mt-1" size={20} data-id="element-299" data-mp="3j8rggouu2vov" />
              <div className="space-y-3" data-id="element-300" data-mp="3eikt0sc25h87">
                <p className="text-[#0F4C5C] font-medium text-base leading-relaxed" data-id="element-301" data-mp="3ehj69qloj6an">
                  DENTAL INSTRUCTOR ATLAS is a complete visual training system for dental instructors, clinical
                  faculty, dental school educators and dental assisting and hygiene program instructors — from
                  beginner to advanced.
                </p>
                <p className="text-[#0A6464] font-semibold text-sm leading-relaxed" data-id="element-302" data-mp="de5nxbo417i8">
                  Every page gives you an illustration, labeled diagram or clinical sequence, a step-by-step
                  breakdown, a ❌ common mistake vs. ✅ correct technique comparison, a teaching cue, a drill,
                  a quick check and an Easy / Standard / Advanced variant — all in one place.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10" data-id="element-303" data-mp="11buxym2q9oze">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5" data-id="element-304" data-mp="11b30f5pklh6h">
            {teachingFlow.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-[#0F4C5C]" data-id="element-305" data-mp="163cwz4l6xfhq">
                {line}
              </li>)}
          </ul>
          <p className="mt-8 text-lg md:text-xl font-bold text-[#0F4C5C]" data-id="element-306" data-mp="1g225w7v3zplu">
            Make Your Next Dental Session Easier to Prepare and More Visual to Teach.
          </p>
          <div className="flex justify-center mt-5" data-id="element-307" data-mp="3h2guxqfrhnus">
            <Button href={CHECKOUT_URL} onLinkClick={trackCheckout} className="text-sm sm:text-lg py-3 sm:py-4 px-5 sm:px-8" data-id="element-308" data-mp="3h0w0e0tg64wx">
              GET MY COMPLETE 6-IN-1 DENTAL TRAINING SYSTEM NOW!
            </Button>
          </div>
        </div>

        <div className="mt-12" data-id="element-309" data-mp="3l3aey8q32iie">
          <p className="text-[#0A6464] text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-310" data-mp="3nty7bere79v">
            The System Covers Six Essential Dental Teaching Areas
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-311" data-mp="3uwhnu3k18gn8">
            {teachingAreas.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-slate-200" data-id="element-312" data-mp="8ngczpa8jvks">
                <span className="text-sm font-bold text-[#0A6464] mb-1" data-id="element-313" data-mp="2ehyg44p93izo">{item.number}</span>
                <span className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-[#0F4C5C]" data-id="element-314" data-mp="2agmppwyjytqf">
                  {item.step}
                </span>
                <span className="text-sm text-slate-700/80 mt-1" data-id="element-315" data-mp="24awxmk3nfhx2">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-[#0F4C5C]" data-id="element-316" data-mp="1qipdpsxgrlfs">
            SIX AREAS. SIX COMPLETE VOLUMES. ONE PROFESSIONAL TRAINING SYSTEM.
          </p>
        </div>
      </div>
    </section>;
}